/**
 * InviteTable Component
 * Displays admin invitations in a table format
 */

import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal, Trash2, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import type { AdminInvite, InviteStatus } from "@/types/invite";

interface InviteTableProps {
  invites: AdminInvite[];
  onRevoke: (id: string) => void;
  onResend?: (id: string) => void;
  isLoading?: boolean;
}

const getStatusBadge = (status: InviteStatus) => {
  const variantMap: Record<
    InviteStatus,
    "default" | "secondary" | "destructive" | "outline"
  > = {
    Pending: "default",
    Accepted: "outline",
    Expired: "secondary",
    Revoked: "destructive",
  };

  const colorMap: Record<InviteStatus, string> = {
    Pending: "",
    Accepted: "border-ok text-ok-ink",
    Expired: "",
    Revoked: "",
  };

  return (
    <Badge variant={variantMap[status]} className={colorMap[status] || ""}>
      {status}
    </Badge>
  );
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "N/A";
  }
};

export function InviteTable({
  invites,
  onRevoke,
  onResend,
  isLoading,
}: InviteTableProps) {
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);

  const handleRevokeClick = (inviteId: string) => {
    setSelectedInviteId(inviteId);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = () => {
    if (selectedInviteId) {
      onRevoke(selectedInviteId);
      setRevokeDialogOpen(false);
      setSelectedInviteId(null);
    }
  };

  if (invites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground">No invitations found</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Invited By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell className="font-medium">{invite.email}</TableCell>
                <TableCell>{invite.role}</TableCell>
                <TableCell>
                  {invite.invitedByName ||
                    invite.invitedByEmail ||
                    invite.invitedBy}
                </TableCell>
                <TableCell>{getStatusBadge(invite.status)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(invite.invitedAt)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {invite.expiresAt ? formatDate(invite.expiresAt) : "Never"}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" disabled={isLoading}>
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      {invite.status === "Pending" && (
                        <>
                          {onResend && (
                            <DropdownMenuItem
                              onClick={() => onResend(invite.id)}
                            >
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Resend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleRevokeClick(invite.id)}
                            className="text-destructive-ink"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Revoke
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to revoke this invitation? This action
              cannot be undone. The recipient will no longer be able to accept
              this invitation.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeConfirm}>
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
